import gql from 'graphql-tag'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import {
  dataAdded,
  dataFetched,
  dataRemoved,
  makesSubscriptions,
} from 'data/graphql'
import { selectors as UISelectors } from 'data/ui'
import {
  channelAttrsFragment,
  channelAttrsForSubscriptionsFragment,
} from 'remote/channels'
import { actions as ConfigActions } from 'data/config'

const channelUpdateSubscription = gql`
  subscription channelUpdated($organizationId: ID!) {
    channelUpdated(organizationId: $organizationId) {
      ...channelAttrsForSubscriptions
    }
  }
  ${channelAttrsForSubscriptionsFragment}
`

const channelActivityUpdateSubscription = gql`
  subscription channelActivityUpdated($organizationId: ID!) {
    channelActivityUpdated(organizationId: $organizationId) {
      id
      __typename
      activityCount
      lastActivityAt
      lastActivityBy
    }
  }
`

const channelCreationSubscription = gql`
  subscription channelCreated($organizationId: ID!) {
    channelCreated(organizationId: $organizationId) {
      ...channelAttrs
    }
  }
  ${channelAttrsFragment}
`

const currentUserChannelRoleChangedSubscription = gql`
  subscription currentUserChannelRoleChanged {
    currentUserChannelRoleChanged {
      __typename
      ... on ChannelRoleChangeInterface {
        previousRole
        nextRole
      }
      ... on ChannelRoleSwitch {
        channel {
          ...channelAttrs
        }
      }
      ... on ChannelRoleRemoval {
        channelId
      }
    }
  }
  ${channelAttrsFragment}
`

const channelDeleteSubscription = gql`
  subscription channelDeleted($organizationId: ID!) {
    channelDeleted(organizationId: $organizationId) {
      __typename
      id
    }
  }
`

export const providesChannelsSubscriptions = compose(
  connect(
    state => ({ organizationId: UISelectors.getActiveOrganizationId(state) }),
    {
      dataAdded,
      dataFetched,
      dataRemoved,
      fetchSearchToken: ConfigActions.fetchSearchToken,
    }
  ),
  makesSubscriptions({
    query: channelUpdateSubscription,
    mapPropsToVariables: ({ organizationId }) => ({
      organizationId,
    }),
    getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
      dataFetched(result.data.channelUpdated, meta)
    },
  }),
  makesSubscriptions({
    query: channelActivityUpdateSubscription,
    mapPropsToVariables: ({ organizationId }) => ({
      organizationId,
    }),
    getOnResultFromProps: ({ dataFetched }) => ({ result, meta }) => {
      dataFetched(result.data.channelActivityUpdated, meta)
    },
  }),
  makesSubscriptions({
    query: channelCreationSubscription,
    mapPropsToVariables: ({ organizationId }) => ({
      organizationId,
    }),
    getOnResultFromProps: ({ dataAdded, fetchSearchToken }) => ({
      result,
      meta,
    }) => {
      dataAdded(result.data.channelCreated, meta)
      fetchSearchToken()
    },
  }),
  makesSubscriptions({
    query: channelDeleteSubscription,
    mapPropsToVariables: ({ organizationId }) => ({
      organizationId,
    }),
    getOnResultFromProps: ({ dataRemoved }) => ({ result, meta }) => {
      dataRemoved(result.data.channelDeleted, meta)
    },
  }),
  makesSubscriptions({
    query: currentUserChannelRoleChangedSubscription,
    mapPropsToVariables: () => ({}),
    getOnResultFromProps: ({ dataFetched, dataRemoved }) => ({
      result,
      meta,
    }) => {
      if (
        result.data.currentUserChannelRoleChanged.__typename ===
        'ChannelRoleRemoval'
      ) {
        dataRemoved({
          __typename: 'Channel',
          id: result.data.currentUserChannelRoleChanged.channelId,
        })
      } else {
        dataFetched(result.data.currentUserChannelRoleChanged.channel, meta)
      }
    },
  })
)
